// src/pages/NormMismatchPage.jsx
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../context/AuthContext";
import { Table } from "react-super-responsive-table";

import {
  AlertTriangle,
  Loader2,
  PackageX,
  Printer,
  SlidersHorizontal,
  TrendingUp,
  X,
} from "lucide-react";

import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import "../styles/TableStyles.css";

/* ------------------------------------------------------------------ */
/*                       Kategorie přehledu                           */
/* ------------------------------------------------------------------ */

const CATEGORY_OPTIONS = [
  {
    key: "noMovementWithNorm",
    label: "Bez pohybu, ale s normou",
    shortLabel: "Bez pohybu s normou",
    description:
      "Přípravky s nastavenou normou, které nemají žádný výdej za posledních 6 měsíců.",
    icon: PackageX,
  },
  {
    key: "normTooHigh",
    label: "Norma nad spotřebou",
    shortLabel: "Norma nad spotřebou",
    description:
      "Aktuální norma je nejméně o 3 kusy a současně o více než 50 % vyšší než doporučená norma.",
    icon: TrendingUp,
  },
  {
    key: "lowStock",
    label: "Riziko nedostatku",
    shortLabel: "Riziko nedostatku",
    description:
      "Aktuální skladová zásoba nepokrývá ani 80 % cílové zásoby na 14 dnů.",
    icon: AlertTriangle,
  },
];

const DEFAULT_CATEGORY_FILTERS = {
  noMovementWithNorm: true,
  normTooHigh: false,
  lowStock: false,
};

const RANGE_FILTERS_DEFAULT = {
  aktualniNorma: {
    mode: "",
    value1: "",
    value2: "",
  },
  doporucenaNorma: {
    mode: "",
    value1: "",
    value2: "",
  },
  vydej6m: {
    mode: "",
    value1: "",
    value2: "",
  },
  sklad: {
    mode: "",
    value1: "",
    value2: "",
  },
  rozdil: {
    mode: "",
    value1: "",
    value2: "",
  },
  pokrytiDni: {
    mode: "",
    value1: "",
    value2: "",
  },
};

/* ------------------------------------------------------------------ */
/*                           Pomocné funkce                           */
/* ------------------------------------------------------------------ */

function normalizeNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value)
      ? value
      : null;
  }

  const cleaned = String(value)
    .replace(/\u00A0/g, "")
    .replace(/\s+/g, "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");

  if (!cleaned) return null;

  const parsed = Number(cleaned);

  return Number.isFinite(parsed)
    ? parsed
    : null;
}

function compareByMode(
  itemValue,
  mode,
  value1,
  value2
) {
  if (!mode) return true;

  if (
    itemValue === null ||
    itemValue === undefined
  ) {
    return false;
  }

  if (mode === "gt") {
    return itemValue > value1;
  }

  if (mode === "gte") {
    return itemValue >= value1;
  }

  if (mode === "lt") {
    return itemValue < value1;
  }

  if (mode === "lte") {
    return itemValue <= value1;
  }

  if (mode === "between") {
    if (
      value1 === null ||
      value2 === null
    ) {
      return true;
    }

    const min = Math.min(value1, value2);
    const max = Math.max(value1, value2);

    return (
      itemValue >= min &&
      itemValue <= max
    );
  }

  return true;
}

function formatNumber(
  value,
  maximumFractionDigits = 2
) {
  const number = normalizeNumber(value);

  if (number === null) return "";

  return number.toLocaleString("cs-CZ", {
    maximumFractionDigits,
  });
}

function formatDateTime(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date
    .toLocaleString("cs-CZ", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    .replace(/\. /g, ".");
}

function categoryLabels(categories) {
  if (!Array.isArray(categories)) {
    return "";
  }

  return CATEGORY_OPTIONS
    .filter((option) =>
      categories.includes(option.key)
    )
    .map((option) => option.shortLabel)
    .join(", ");
}

/* ------------------------------------------------------------------ */
/*                             Komponenta                             */
/* ------------------------------------------------------------------ */

export default function NormMismatchPage() {
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [lastUpdated, setLastUpdated] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [
    categoryFilters,
    setCategoryFilters,
  ] = useState(DEFAULT_CATEGORY_FILTERS);

  const [
    rangeFilters,
    setRangeFilters,
  ] = useState(RANGE_FILTERS_DEFAULT);

  const [
    showAdvancedFilters,
    setShowAdvancedFilters,
  ] = useState(false);

  const [sortField, setSortField] =
    useState("severity");

  const [
    sortDirection,
    setSortDirection,
  ] = useState("desc");

  /* ---------------------------------------------------------------- */
  /*                         Načtení dat                              */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let active = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          "/api/norm-mismatch/analysis",
          {
            method: "GET",
            credentials: "include",
          }
        );

        const contentType =
          response.headers.get("content-type") ||
          "";

        const payload =
          contentType.includes("application/json")
            ? await response.json()
            : null;

        if (!response.ok) {
          throw new Error(
            payload?.error ||
              "Nepodařilo se načíst analýzu norem."
          );
        }

        if (!active) return;

        const sourceItems =
          Array.isArray(payload?.items)
            ? payload.items
            : [];

        const normalized = sourceItems.map(
          (item) => ({
            pdk:
              item.pdk ?? "",

            nazev:
              item.nazev ?? "",

            categories:
              Array.isArray(item.categories)
                ? item.categories
                : [],

            kategorie:
              categoryLabels(
                item.categories
              ),

            aktualniNorma:
              normalizeNumber(
                item.aktualniNorma
              ) ?? 0,

            doporucenaNorma:
              normalizeNumber(
                item.doporucenaNorma
              ) ?? 0,

            vydej6m:
              normalizeNumber(
                item.vydej6m
              ) ?? 0,

            sklad:
              normalizeNumber(
                item.sklad
              ) ?? 0,

            rozdil:
              normalizeNumber(
                item.rozdil
              ) ?? 0,

            pokrytiDni:
              normalizeNumber(
                item.pokrytiDni
              ),

            duvod:
              item.duvod ?? "",

            severity:
              normalizeNumber(
                item.severity
              ) ?? 0,
          })
        );

        setData(normalized);
        setSummary(
          payload?.summary || {}
        );

        setLastUpdated(
          payload?.lastBatch ||
            payload?.generatedAt ||
            null
        );
      } catch (err) {
        console.error(err);

        if (active) {
          setData([]);
          setSummary({});

          setError(
            err.message ||
              "Nepodařilo se načíst analýzu norem."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [user]);

  /* ---------------------------------------------------------------- */
  /*                  Aktivní kategorie a popisky                    */
  /* ---------------------------------------------------------------- */

  const selectedCategoryKeys = useMemo(
    () =>
      CATEGORY_OPTIONS
        .filter(
          (option) =>
            categoryFilters[option.key]
        )
        .map(
          (option) => option.key
        ),
    [categoryFilters]
  );

  const activeCategoryLabels = useMemo(
    () =>
      CATEGORY_OPTIONS
        .filter(
          (option) =>
            categoryFilters[option.key]
        )
        .map(
          (option) => option.label
        ),
    [categoryFilters]
  );

  /* ---------------------------------------------------------------- */
  /*                         Číselné filtry                           */
  /* ---------------------------------------------------------------- */

  const applyNumberRangeFilter = (
    items,
    key
  ) => {
    const filter = rangeFilters[key];

    if (!filter?.mode) {
      return items;
    }

    const value1 =
      normalizeNumber(filter.value1);

    const value2 =
      normalizeNumber(filter.value2);

    if (
      filter.mode === "between" &&
      (
        value1 === null ||
        value2 === null
      )
    ) {
      return items;
    }

    if (
      filter.mode !== "between" &&
      value1 === null
    ) {
      return items;
    }

    return items.filter((item) => {
      const itemValue =
        normalizeNumber(item[key]);

      return compareByMode(
        itemValue,
        filter.mode,
        value1,
        value2
      );
    });
  };

  /* ---------------------------------------------------------------- */
  /*                     Filtrování a řazení                         */
  /* ---------------------------------------------------------------- */

  const filteredAndSortedData =
    useMemo(() => {
      let result = [...data];

      /*
       * Více aktivních zaškrtávek funguje jako NEBO.
       * Položka se zobrazí, pokud odpovídá alespoň
       * jedné aktivní kategorii.
       */
      result = result.filter((item) =>
        item.categories.some((category) =>
          selectedCategoryKeys.includes(
            category
          )
        )
      );

      const normalizedSearch =
        search
          .trim()
          .toLocaleLowerCase("cs");

      if (normalizedSearch) {
        result = result.filter(
          (item) =>
            String(item.pdk || "")
              .toLocaleLowerCase("cs")
              .includes(
                normalizedSearch
              ) ||
            String(item.nazev || "")
              .toLocaleLowerCase("cs")
              .includes(
                normalizedSearch
              )
        );
      }

      result = applyNumberRangeFilter(
        result,
        "aktualniNorma"
      );

      result = applyNumberRangeFilter(
        result,
        "doporucenaNorma"
      );

      result = applyNumberRangeFilter(
        result,
        "vydej6m"
      );

      result = applyNumberRangeFilter(
        result,
        "sklad"
      );

      result = applyNumberRangeFilter(
        result,
        "rozdil"
      );

      result = applyNumberRangeFilter(
        result,
        "pokrytiDni"
      );

      result.sort((a, b) => {
        const aValue =
          a?.[sortField];

        const bValue =
          b?.[sortField];

        const numericFields = [
          "aktualniNorma",
          "doporucenaNorma",
          "vydej6m",
          "sklad",
          "rozdil",
          "pokrytiDni",
          "severity",
        ];

        if (
          numericFields.includes(
            sortField
          )
        ) {
          const aNumber =
            normalizeNumber(aValue) ??
            -Infinity;

          const bNumber =
            normalizeNumber(bValue) ??
            -Infinity;

          return sortDirection === "asc"
            ? aNumber - bNumber
            : bNumber - aNumber;
        }

        const aString =
          String(aValue ?? "")
            .toLocaleLowerCase("cs");

        const bString =
          String(bValue ?? "")
            .toLocaleLowerCase("cs");

        return sortDirection === "asc"
          ? aString.localeCompare(
              bString,
              "cs"
            )
          : bString.localeCompare(
              aString,
              "cs"
            );
      });

      return result;
    }, [
      data,
      search,
      selectedCategoryKeys,
      rangeFilters,
      sortField,
      sortDirection,
    ]);

  /* ---------------------------------------------------------------- */
  /*                         Ovládání filtrů                          */
  /* ---------------------------------------------------------------- */

  const toggleCategory = (key) => {
    setCategoryFilters((previous) => {
      const activeCount =
        Object.values(previous).filter(
          Boolean
        ).length;

      /*
       * Nenecháme uživatele vypnout
       * poslední aktivní kategorii.
       */
      if (
        previous[key] &&
        activeCount === 1
      ) {
        return previous;
      }

      return {
        ...previous,
        [key]: !previous[key],
      };
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((previous) =>
        previous === "asc"
          ? "desc"
          : "asc"
      );

      return;
    }

    setSortField(field);

    const numericFields = [
      "aktualniNorma",
      "doporucenaNorma",
      "vydej6m",
      "sklad",
      "rozdil",
      "pokrytiDni",
      "severity",
    ];

    setSortDirection(
      numericFields.includes(field)
        ? "desc"
        : "asc"
    );
  };

  const handleRangeFilterChange = (
    key,
    field,
    value
  ) => {
    setRangeFilters((previous) => ({
      ...previous,
      [key]: {
        ...previous[key],
        [field]: value,
      },
    }));
  };

  const clearFilters = () => {
    setSearch("");

    setCategoryFilters(
      DEFAULT_CATEGORY_FILTERS
    );

    setRangeFilters(
      RANGE_FILTERS_DEFAULT
    );

    setSortField("severity");
    setSortDirection("desc");
  };

  const handlePrint = () => {
    window.print();
  };

  /* ---------------------------------------------------------------- */
  /*                    Vykreslení číselného filtru                  */
  /* ---------------------------------------------------------------- */

  const renderRangeFilter = (
    key,
    label
  ) => {
    const filter =
      rangeFilters[key];

    const isBetween =
      filter.mode === "between";

    return (
      <div className="border rounded-xl p-3 bg-white shadow-sm">
        <div className="text-xs font-semibold text-gray-600 mb-2">
          {label}
        </div>

        <select
          value={filter.mode}
          onChange={(event) =>
            handleRangeFilterChange(
              key,
              "mode",
              event.target.value
            )
          }
          className="border p-2 rounded w-full mb-2"
        >
          <option value="">
            Bez filtru
          </option>

          <option value="gt">
            Větší než
          </option>

          <option value="gte">
            Větší nebo rovno
          </option>

          <option value="lt">
            Menší než
          </option>

          <option value="lte">
            Menší nebo rovno
          </option>

          <option value="between">
            Interval
          </option>
        </select>

        <div className="flex gap-2">
          <input
            type="number"
            step="any"
            value={filter.value1}
            onChange={(event) =>
              handleRangeFilterChange(
                key,
                "value1",
                event.target.value
              )
            }
            placeholder={
              isBetween
                ? "Od"
                : "Hodnota"
            }
            className="border p-2 rounded w-full min-w-0"
          />

          {isBetween && (
            <input
              type="number"
              step="any"
              value={filter.value2}
              onChange={(event) =>
                handleRangeFilterChange(
                  key,
                  "value2",
                  event.target.value
                )
              }
              placeholder="Do"
              className="border p-2 rounded w-full min-w-0"
            />
          )}
        </div>
      </div>
    );
  };

  const renderSortMark = (field) => {
    if (sortField !== field) {
      return "";
    }

    return sortDirection === "asc"
      ? " ▲"
      : " ▼";
  };

  /* ---------------------------------------------------------------- */
  /*                              JSX                                 */
  /* ---------------------------------------------------------------- */

  return (
    <div
      id="norm-mismatch-print-root"
      className="p-4 print:p-0"
    >
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 6mm;
          }

          body * {
            visibility: hidden !important;
          }

          #norm-mismatch-print-root,
          #norm-mismatch-print-root * {
            visibility: visible !important;
          }

          #norm-mismatch-print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .no-print {
            display: none !important;
          }

          .print-only {
            display: block !important;
          }

          .print-table-wrap {
            overflow: visible !important;
          }

          .custom-table {
            width: 100% !important;
            border-collapse: collapse !important;
            table-layout: fixed !important;
            font-size: 6.3px !important;
          }

          .custom-table th,
          .custom-table td {
            border: 1px solid #666 !important;
            padding: 2.5px 2px !important;
            color: #000 !important;
            background: #fff !important;
            vertical-align: top !important;
          }

          .custom-table th {
            font-weight: 700 !important;
            white-space: normal !important;
            line-height: 1.05 !important;
          }

          /* PDK */
          .custom-table th:nth-child(1),
          .custom-table td:nth-child(1) {
            width: 9% !important;
            white-space: nowrap !important;
          }

          /* Název */
          .custom-table th:nth-child(2),
          .custom-table td:nth-child(2) {
            width: 24% !important;
            font-size: 6.1px !important;
            line-height: 1.1 !important;
            word-break: break-word !important;
          }

          /* Kategorie */
          .custom-table th:nth-child(3),
          .custom-table td:nth-child(3) {
            width: 19% !important;
            font-size: 5.9px !important;
            line-height: 1.08 !important;
            word-break: break-word !important;
          }

          /* Číselné sloupce */
          .custom-table th:nth-child(n+4),
          .custom-table td:nth-child(n+4) {
            width: 8% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
        }

        @media screen {
          .print-only {
            display: none !important;
          }

          .norm-num {
            text-align: right;
            white-space: nowrap;
          }

          .norm-name {
            min-width: 290px;
          }

          .norm-category {
            min-width: 210px;
          }
        }
      `}</style>

      <div className="flex flex-col gap-4 mb-4 no-print">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-2xl font-semibold">
              Analýza norem
            </h2>

            {lastUpdated ? (
              <span className="text-sm text-gray-500">
                Poslední export:{" "}
                {formatDateTime(
                  lastUpdated
                )}
              </span>
            ) : !loading ? (
              <span className="text-sm text-rose-600">
                Zatím nebyl nahrán žádný export
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() =>
                setShowAdvancedFilters(
                  (previous) =>
                    !previous
                )
              }
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
            >
              <SlidersHorizontal className="w-4 h-4" />

              {showAdvancedFilters
                ? "Skrýt filtry"
                : "Další filtry"}
            </button>

            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
            >
              <X className="w-4 h-4" />
              Vymazat
            </button>

            <button
              type="button"
              onClick={handlePrint}
              disabled={
                filteredAndSortedData.length ===
                0
              }
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Printer className="w-4 h-4" />
              Tisk / PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,1fr)_auto] gap-3 items-end">
          <div className="border rounded-xl p-3 bg-white shadow-sm">
            <div className="text-xs font-semibold text-gray-600 mb-2">
              HLEDAT
            </div>

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Název / PDK"
              className="border p-2 rounded w-full"
            />
          </div>

          <div className="text-sm text-gray-500 pb-3">
            Zobrazeno záznamů:{" "}
            {filteredAndSortedData.length}{" "}
            / {data.length}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {CATEGORY_OPTIONS.map(
            (option) => {
              const Icon =
                option.icon;

              const checked =
                Boolean(
                  categoryFilters[
                    option.key
                  ]
                );

              const count =
                Number(
                  summary?.[
                    option.key
                  ] || 0
                );

              return (
                <label
                  key={option.key}
                  className={`cursor-pointer rounded-xl border p-3 shadow-sm transition ${
                    checked
                      ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200"
                      : "border-gray-200 bg-white hover:border-blue-200"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        toggleCategory(
                          option.key
                        )
                      }
                      className="mt-1 h-4 w-4"
                    />

                    <Icon
                      className={`mt-0.5 h-5 w-5 shrink-0 ${
                        checked
                          ? "text-blue-700"
                          : "text-gray-500"
                      }`}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-gray-900">
                          {option.label}
                        </span>

                        <span className="rounded-full bg-white border px-2 py-0.5 text-xs font-semibold text-gray-700">
                          {count}
                        </span>
                      </div>

                      <p className="mt-1 text-xs leading-5 text-gray-500">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </label>
              );
            }
          )}
        </div>

        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
            {renderRangeFilter(
              "aktualniNorma",
              "AKTUÁLNÍ NORMA"
            )}

            {renderRangeFilter(
              "doporucenaNorma",
              "DOPORUČENÁ NORMA"
            )}

            {renderRangeFilter(
              "vydej6m",
              "VÝDEJ 6M"
            )}

            {renderRangeFilter(
              "sklad",
              "SKLAD"
            )}

            {renderRangeFilter(
              "rozdil",
              "ROZDÍL"
            )}

            {renderRangeFilter(
              "pokrytiDni",
              "KRYTÍ VE DNECH"
            )}
          </div>
        )}
      </div>

      <div className="print-only mb-4">
        <h1 className="text-xl font-bold">
          Analýza norem
        </h1>

        <div className="text-sm mt-1">
          Pobočka:{" "}
          {String(
            user?.pharmacyCode || ""
          ).replace(/^0+/, "") || "-"}
        </div>

        <div className="text-sm">
          Vytištěno:{" "}
          {formatDateTime(new Date())}
        </div>

        {lastUpdated ? (
          <div className="text-sm">
            Poslední export:{" "}
            {formatDateTime(
              lastUpdated
            )}
          </div>
        ) : null}

        <div className="text-sm">
          Aktivní přehledy:{" "}
          {activeCategoryLabels.join(
            ", "
          )}
        </div>

        {search.trim() ? (
          <div className="text-sm">
            Hledaný výraz:{" "}
            {search.trim()}
          </div>
        ) : null}

        <div className="text-sm font-semibold mt-1">
          Počet položek:{" "}
          {filteredAndSortedData.length}
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Načítám data...
        </div>
      ) : filteredAndSortedData.length ===
        0 ? (
        <p>
          Pro zvolené filtry nebyly nalezeny žádné položky.
        </p>
      ) : (
        <div className="print-table-wrap overflow-x-auto">
          <Table className="custom-table">
            <thead>
              <tr>
                <th
                  onClick={() =>
                    handleSort("pdk")
                  }
                  className="cursor-pointer select-none"
                >
                  PDK
                  {renderSortMark("pdk")}
                </th>

                <th
                  onClick={() =>
                    handleSort("nazev")
                  }
                  className="cursor-pointer select-none"
                >
                  NÁZEV
                  {renderSortMark("nazev")}
                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "kategorie"
                    )
                  }
                  className="cursor-pointer select-none"
                >
                  KATEGORIE
                  {renderSortMark(
                    "kategorie"
                  )}
                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "aktualniNorma"
                    )
                  }
                  className="cursor-pointer select-none"
                >
                  AKT. NORMA
                  {renderSortMark(
                    "aktualniNorma"
                  )}
                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "doporucenaNorma"
                    )
                  }
                  className="cursor-pointer select-none"
                >
                  DOP. NORMA
                  {renderSortMark(
                    "doporucenaNorma"
                  )}
                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "vydej6m"
                    )
                  }
                  className="cursor-pointer select-none"
                >
                  VÝDEJ 6M
                  {renderSortMark(
                    "vydej6m"
                  )}
                </th>

                <th
                  onClick={() =>
                    handleSort("sklad")
                  }
                  className="cursor-pointer select-none"
                >
                  SKLAD
                  {renderSortMark(
                    "sklad"
                  )}
                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "rozdil"
                    )
                  }
                  className="cursor-pointer select-none"
                >
                  ROZDÍL
                  {renderSortMark(
                    "rozdil"
                  )}
                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "pokrytiDni"
                    )
                  }
                  className="cursor-pointer select-none"
                >
                  KRYTÍ DNŮ
                  {renderSortMark(
                    "pokrytiDni"
                  )}
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredAndSortedData.map(
                (item, index) => (
                  <tr
                    key={`${
                      item.pdk ||
                      item.nazev ||
                      "row"
                    }-${index}`}
                  >
                    <td>
                      {item.pdk || ""}
                    </td>

                    <td className="norm-name">
                      {item.nazev || ""}
                    </td>

                    <td className="norm-category">
                      {item.kategorie || ""}
                    </td>

                    <td className="norm-num">
                      {formatNumber(
                        item.aktualniNorma
                      )}
                    </td>

                    <td className="norm-num">
                      {formatNumber(
                        item.doporucenaNorma
                      )}
                    </td>

                    <td className="norm-num">
                      {formatNumber(
                        item.vydej6m
                      )}
                    </td>

                    <td className="norm-num">
                      {formatNumber(
                        item.sklad
                      )}
                    </td>

                    <td className="norm-num">
                      {formatNumber(
                        item.rozdil
                      )}
                    </td>

                    <td className="norm-num">
                      {item.pokrytiDni ===
                      null
                        ? ""
                        : formatNumber(
                            item.pokrytiDni,
                            1
                          )}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}